import { Grid, Card, CardContent, Typography, IconButton, Collapse, Divider } from '@material-ui/core';
import { Cancel, CheckCircle, ExpandLess, ExpandMore } from '@material-ui/icons';
import {
  PagamentoResponse,
  Fechamento,
  InvestidorResumo,
  ParticipanteResumo,
  InstrumentoResumo,
  InstrumentoFinanceiro,
} from 'common/genericTypes';
import { useGenericStyles } from 'common/genericStyles';
import { eventoFinanceiroDescriptions, pagamentoStatusDescriptions } from 'common/genericDescriptions';
import { LgSpace, SmSpace, MdSpace, Button } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { formatDate } from 'utils/date';
import { formatCurrencyNumber, formatEightDecimalsNumber, formatNumber } from 'utils/number';
import { addCpfCnpjMask } from 'utils/mask';
import { GenericDrawer } from 'components/genericDrawer';
import { RootState } from 'store';
import { formatInstrumentoCurrency, isDivisible } from 'utils/instrumentoFinanceiroUtils';

interface FechamentoPorCustodiante {
  custodiante: ParticipanteResumo;
  fechamentos: Array<Fechamento>;
  qtdTotal: number;
  valorTotal: number;
}

type ComponentParams = { instrumentoId: string; eventoId: string };
type Props = RouteComponentProps<ComponentParams>;

const LiquidacaoEventos: React.FC<Props> = (props) => {
  const [pagamento, setPagamento] = useState<PagamentoResponse>();
  const [fechamentosPorCustodiante, setFechamentosPorCustodiante] = useState<Array<FechamentoPorCustodiante>>([]);
  const [valorMonetarioTotal, setValorMonetarioTotal] = useState<number>(0);
  const [finished, setFinished] = useState<boolean>(false);
  const [confirmar, setConfirmar] = useState<boolean>(true);
  const [expandedId, setExpandedId] = useState<string>();
  const [openConfirmarLiquidacaoDrawer, setOpenConfirmarLiquidacaoDrawer] = useState(false);
  const [openCancelarLiquidacaoDrawer, setOpenCancelarLiquidacaoDrawer] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useGenericStyles();
  const {
    getPagamento: getPagamentoApi,
    postConfirmarLiquidacaoPagamento,
    postCancelarLiquidacaoPagamento,
  } = useSelector((state: RootState) => state.instrumentoApi);
  const {
    match: {
      params: { instrumentoId },
    },
  } = props;

  const handleConfirmarLiquidacao = async () => {
    try {
      if (pagamento) {
        await postConfirmarLiquidacaoPagamento(dispatch, pagamento.id, true);
        setFinished(true);
        handleCloseConfirmarLiquidacaoDrawer();
      }
    } catch (error) { }
  };

  const handleOpenConfirmarLiquidacaoDrawer = () => {
    setOpenConfirmarLiquidacaoDrawer(true);
  };

  const handleCloseConfirmarLiquidacaoDrawer = () => {
    setOpenConfirmarLiquidacaoDrawer(false);
  };

  const handleCancelarLiquidacao = async () => {
    try {
      if (pagamento) {
        await postCancelarLiquidacaoPagamento(dispatch, pagamento.id, true);
        setFinished(true);
        handleCloseCancelarLiquidacaoDrawer();
      }
    } catch (error) { }
  };

  const handleOpenCancelarLiquidacaoDrawer = () => {
    setOpenCancelarLiquidacaoDrawer(true);
  };

  const handleCloseCancelarLiquidacaoDrawer = () => {
    setOpenCancelarLiquidacaoDrawer(false);
  };

  const getConfirmar = useCallback(async () => {
    let queryStrings = props.location.search;
    let searchParams = new URLSearchParams(queryStrings);
    setConfirmar(searchParams.get('confirmar') === 'true');
  }, []);

  const getPagamento = useCallback(async () => {
    try {
      const result = await getPagamentoApi(dispatch, props.match.params.eventoId, true);
      setPagamento(result);
      if (result.fechamentos?.length > 0) {
        let fechamentos: Array<FechamentoPorCustodiante> = [];
        let valorTotalAtivos: number = 0;
        const custodiantesUnicos = Array.from(new Set(result.fechamentos.map((f) => f.custodiante.id)))
          .map((id) => result.fechamentos.find((_) => _.custodiante.id === id)?.custodiante)
          .filter((_) => _ !== undefined)
          .map((_) => _ as ParticipanteResumo);

        const investidoresUnicos = Array.from(new Set(result.fechamentos.map((f) => f.investidor.id)))
          .map((id) => result.fechamentos.find((_) => _.investidor.id === id)?.investidor)
          .filter((_) => _ !== undefined)
          .map((_) => _ as InvestidorResumo);

        for (let i = 0; i < custodiantesUnicos.length; i++) {
          const filtrados = result.fechamentos.filter((_) => _.custodiante.id === custodiantesUnicos[i].id);
          const valorTotal = filtrados.map((_) => _.quantidade * _.precoUnitario).reduce((a, b) => a + b, 0);
          let qtdTotal = 0;

          for (let j = 0; j < investidoresUnicos.length; j++) {
            qtdTotal += filtrados.find((_) => _.investidor.id === investidoresUnicos[j].id)?.quantidade ?? 0;
          }

          fechamentos.push({
            custodiante: custodiantesUnicos[i],
            fechamentos: filtrados,
            valorTotal,
            qtdTotal,
          });

          valorTotalAtivos += valorTotal;
        }
        setFechamentosPorCustodiante(fechamentos);
        setValorMonetarioTotal(valorTotalAtivos);
      }
    } catch (error) { }
  }, [dispatch, getPagamentoApi, instrumentoId]);

  const AprovarPagamentoDrawer = (
    <GenericDrawer
      opened={openConfirmarLiquidacaoDrawer}
      onClose={handleCloseConfirmarLiquidacaoDrawer}
      title={`Confirmar Liquidação`}
      buttons={[
        {
          buttonText: `Confirmar Liquidação`,
          buttonAction: handleConfirmarLiquidacao,
          buttonColor: 'primary',
        },
        {
          buttonText: 'Cancelar',
          buttonAction: handleCloseConfirmarLiquidacaoDrawer,
        },
      ]}
    >
      <>
        <Typography variant="body2" display={'inline'}>
          {`Tem certeza que deseja confirmar a liquidação do instrumento financeiro`}
        </Typography>
        &nbsp;<Typography display={'inline'}>{pagamento?.instrumentoFinanceiro.codigo}</Typography>&nbsp;
        <Typography display={'inline'}>{'?'}</Typography>
        <SmSpace />
      </>
    </GenericDrawer>
  );

  const CancelarPagamentoDrawer = (
    <GenericDrawer
      opened={openCancelarLiquidacaoDrawer}
      onClose={handleCloseCancelarLiquidacaoDrawer}
      title={`Cancelar Liquidação`}
      buttons={[
        {
          buttonText: `Cancelar Liquidação`,
          buttonAction: handleCancelarLiquidacao,
          buttonColor: 'error',
        },
        {
          buttonText: 'Cancelar',
          buttonAction: handleCloseCancelarLiquidacaoDrawer,
        },
      ]}
    >
      <>
        <Typography variant="body2" display={'inline'}>
          {`Tem certeza que deseja cancelar a liquidação do instrumento financeiro`}
        </Typography>
        &nbsp;<Typography display={'inline'}>{pagamento?.instrumentoFinanceiro.codigo}</Typography>&nbsp;
        <Typography display={'inline'}>{'?'}</Typography>
        <SmSpace />
      </>
    </GenericDrawer>
  );

  const handleExpand = (id: string) => setExpandedId(id === expandedId ? '' : id);

  const handleInstrumento = (instrumento?: InstrumentoResumo) => {
    switch (instrumento?.tipoInstrumentoFinanceiro) {
      case InstrumentoFinanceiro.Cdca:
        return 'CDCA';
      case InstrumentoFinanceiro.CotasDeFundo:
        return 'Cotas de Fundo';
      case InstrumentoFinanceiro.CertificadoRecebiveis:
        return 'CR';
      case InstrumentoFinanceiro.Cra:
        return 'CRA';
      case InstrumentoFinanceiro.Cri:
        return 'CRI';
      case InstrumentoFinanceiro.Debenture:
        return 'Debênture';
      case InstrumentoFinanceiro.NotaComercial:
        return 'Nota Comercial';
      default:
        return '';
    }
  };

  useEffect(() => {
    getConfirmar();
  }, [getConfirmar]);

  useEffect(() => {
    getPagamento();
  }, [getPagamento]);

  return (
    <div style={{ marginRight: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="h1">
          {`${handleInstrumento(pagamento?.instrumentoFinanceiro)}-${pagamento?.instrumentoFinanceiro.codigo ?? ''}`}
        </Typography>
      </div>
      <LgSpace />
      {pagamento && (
        <Card variant="outlined" style={{ margin: '10px 0' }}>
          <CardContent style={{ paddingBottom: 16 }}>
            <div style={{ display: 'flex' }}>
              <Grid container spacing={8}>
                <Grid item>
                  <Typography variant="body1">{'Data de Vencimento'}</Typography>
                  <Typography variant="body2">{formatDate(pagamento.dataVencimento)}</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1">{'Status'}</Typography>
                  <Typography variant="body2">{pagamentoStatusDescriptions[pagamento.status]}</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1">{'Tipo'}</Typography>
                  <Typography variant="body2">{eventoFinanceiroDescriptions[pagamento.tipo]}</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1">{'Total de ativos'}</Typography>
                  <Typography variant="body2">
                    {isDivisible(pagamento.instrumentoFinanceiro.tipoInstrumentoFinanceiro)
                      ? formatEightDecimalsNumber(pagamento.quantidade ?? pagamento.instrumentoFinanceiro.quantidadeDepositada)
                      : formatNumber(pagamento.quantidade ?? pagamento.instrumentoFinanceiro.quantidadeDepositada, { minimumFractionDigits: 0 })}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1">{'Valor Total de Pagamento'}</Typography>
                  <Typography variant="body2">{formatInstrumentoCurrency(valorMonetarioTotal, pagamento.instrumentoFinanceiro.tipoInstrumentoFinanceiro)}</Typography>
                </Grid>
              </Grid>
            </div>
          </CardContent>
        </Card>
      )}
      <LgSpace />
      {finished ? (
        <Grid container direction="column" justify="center" alignItems="center">
          <div className={classes.iconWrapper}>
            {confirmar ? (
              <CheckCircle style={{ width: '40px', height: '40px' }} color="secondary" />
            ) : (
              <Cancel style={{ width: '40px', height: '40px' }} color="secondary" />
            )}
          </div>
          <SmSpace />
          <Typography variant="h3">{`Liquidação ${confirmar ? 'confirmada' : 'cancelada'} com sucesso!`}</Typography>
          <SmSpace />
          <Typography variant="body2">{'Clique para voltar à listagem de instrumentos financeiros.'}</Typography>
          <MdSpace />
          <Button onClick={() => history.push('/instrumentos/gerenciar')} variant="contained" color="primary">
            {'Voltar para a listagem'}
          </Button>
          <MdSpace />
        </Grid>
      ) : (
        <>
          {fechamentosPorCustodiante.map((pfc) => (
            <Card variant="outlined" style={{ margin: '10px 0' }}>
              <CardContent style={{ paddingBottom: 16 }}>
                <div style={{ display: 'flex' }}>
                  <Grid container>
                    <Grid item md={2}>
                      <Typography variant="body2">{addCpfCnpjMask(pfc.custodiante.cnpj ?? '')}</Typography>
                    </Grid>
                    <Grid item md={7}>
                      <Typography variant="body2">{pfc.custodiante.razaoSocial}</Typography>
                    </Grid>
                    <Grid item md={1}>
                      <Typography variant="body2">{formatNumber(pfc.qtdTotal, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}</Typography>
                    </Grid>
                    <Grid item md={2}>
                      <Typography variant="body2">{formatCurrencyNumber(pfc.valorTotal)}</Typography>
                    </Grid>
                  </Grid>
                  <IconButton
                    aria-expanded={expandedId === pfc.custodiante.id}
                    size="small"
                    onClick={() => handleExpand(pfc.custodiante.id)}
                    style={{ float: 'right' }}
                  >
                    {expandedId === pfc.custodiante.id ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </div>
              </CardContent>
              <Collapse in={expandedId === pfc.custodiante.id} timeout="auto" unmountOnExit>
                <CardContent style={{ paddingTop: 0 }}>
                  <div style={{ height: 16 }}></div>
                  <>
                    <Grid container>
                      <Grid item md={2}>
                        <Typography variant="body1">{'CPF/CNPJ'}</Typography>
                      </Grid>
                      <Grid item md={4}>
                        <Typography variant="body1">{'Razão Social'}</Typography>
                      </Grid>
                      <Grid item md={3}>
                        <Typography variant="body1">{'Tipo'}</Typography>
                      </Grid>
                      <Grid item md={1}>
                        <Typography variant="body1">{'Quantidade'}</Typography>
                      </Grid>
                      <Grid item md={2}>
                        <Typography variant="body1">{'Valor Total'}</Typography>
                      </Grid>
                    </Grid>
                    <SmSpace />
                    <Divider />
                    {pfc.fechamentos.map((fechamento) => (
                      <>
                        <SmSpace />
                        <Grid container>
                          <Grid item md={2}>
                            <Typography variant="body2">{addCpfCnpjMask(fechamento.investidor.cpfCnpj)}</Typography>
                          </Grid>
                          <Grid item md={4}>
                            <Typography variant="body2">{fechamento.investidor.razaoSocial}</Typography>
                          </Grid>
                          <Grid item md={3}>
                            <Typography variant="body2">{eventoFinanceiroDescriptions[fechamento.tipo]}</Typography>
                          </Grid>
                          <Grid item md={1}>
                            <Typography variant="body2">{formatNumber(fechamento.quantidade, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}</Typography>
                          </Grid>
                          <Grid item md={2}>
                            <Typography variant="body2">
                              {formatCurrencyNumber(fechamento.quantidade * fechamento.precoUnitario)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </>
                    ))}
                  </>
                </CardContent>
              </Collapse>
            </Card>
          ))}
          <MdSpace />
          <div style={{ textAlign: 'center' }}>
            {confirmar ? (
              <Button onClick={handleOpenConfirmarLiquidacaoDrawer} variant="contained" color="primary">
                {'Confirmar Liquidação'}
              </Button>
            ) : (
              <Button onClick={handleOpenCancelarLiquidacaoDrawer} variant="contained" color="error">
                {'Cancelar Liquidação'}
              </Button>
            )}
          </div>
        </>
      )}
      <MdSpace />
      {AprovarPagamentoDrawer}
      {CancelarPagamentoDrawer}
    </div>
  );
};

export default LiquidacaoEventos;
